"use client";

import React, { useEffect, useState } from "react";
import AmountInputStep from "./AmountInputStep";
import PaymentMethodStep, { type PaymentMethod } from "./PaymentMethodStep";
import EightyEightPayStep from "./EightyEightPayStep";
import CryptoPaymentOptionStep from "./CryptoPaymentOptionStep";
import DposSessionStep from "./DposSessionStep";
import PayLinkSessionStep from "./PayLinkSessionStep";
import { type DposCryptoFlow } from "../../services/dposService";
import {
  clearCreatePaymentFlowState,
  persistCreatePaymentFlowState,
  readCreatePaymentFlowState,
  type DposCreatePaymentStep,
} from "./dposFlowPersistence";

type Step = DposCreatePaymentStep;

interface CreatePaymentFlowProps {
  onClose: () => void;
  onComplete: (data: { amount: string; method: PaymentMethod }) => void;
}

export default function CreatePaymentFlow({
  onClose,
  onComplete,
}: CreatePaymentFlowProps) {
  const [persistedFlowState] = useState(() => readCreatePaymentFlowState());
  const [step, setStep] = useState<Step>(persistedFlowState?.step || "amount");
  const [amount, setAmount] = useState(persistedFlowState?.amount || "");
  const [cryptoFlow, setCryptoFlow] = useState<DposCryptoFlow | null>(
    persistedFlowState?.cryptoFlow || null
  );

  useEffect(() => {
    if (step === "amount" && !amount && !cryptoFlow) {
      clearCreatePaymentFlowState();
      return;
    }

    persistCreatePaymentFlowState({
      amount,
      step,
      cryptoFlow,
    });
  }, [amount, cryptoFlow, step]);

  const handleAmountContinue = (enteredAmount: string) => {
    setAmount(enteredAmount);
    setStep("method");
  };

  const handleMethodSelect = (method: PaymentMethod) => {
    if (method === "88pay") {
      setStep("88pay");
      return;
    }

    if (method === "crypto") {
      setStep("crypto-option");
      return;
    }

    if (method === "paylink") {
      setStep("paylink-session");
      return;
    }

    handleComplete({ amount, method });
  };

  const handleBack = () => {
    setCryptoFlow(null);
    setStep("amount");
  };

  const handleCryptoOptionSelect = (option: DposCryptoFlow) => {
    setCryptoFlow(option);
    setStep("crypto-session");
  };

  const handleCryptoModeChange = (option: DposCryptoFlow) => {
    setCryptoFlow(option);
  };

  const handleCreateNewPayment = () => {
    setAmount("");
    setCryptoFlow(null);
    setStep("amount");
  };

  const handleClose = () => {
    clearCreatePaymentFlowState();
    onClose();
  };

  const handleComplete = (data: { amount: string; method: PaymentMethod }) => {
    clearCreatePaymentFlowState();
    onComplete(data);
  };

  if (step === "88pay") {
    return (
      <EightyEightPayStep
        amount={amount}
        onBack={() => setStep("method")}
        onClose={handleClose}
        onCreateNewPayment={handleCreateNewPayment}
      />
    );
  }

  if (step === "crypto-option") {
    return (
      <CryptoPaymentOptionStep
        amount={amount}
        onSelect={handleCryptoOptionSelect}
        onBack={() => setStep("method")}
        onClose={handleClose}
      />
    );
  }

  if (step === "crypto-session" && cryptoFlow) {
    return (
      <DposSessionStep
        amount={amount}
        mode={cryptoFlow}
        onModeChange={handleCryptoModeChange}
        onBack={() => setStep("crypto-option")}
        onClose={handleClose}
        onCreateNewPayment={handleCreateNewPayment}
      />
    );
  }

  if (step === "paylink-session") {
    return (
      <PayLinkSessionStep
        amount={amount}
        onBack={() => setStep("method")}
        onClose={handleClose}
        onCreateNewPayment={handleCreateNewPayment}
      />
    );
  }

  if (step === "method") {
    return (
      <PaymentMethodStep
        amount={amount}
        onSelect={handleMethodSelect}
        onBack={handleBack}
        onClose={handleClose}
      />
    );
  }

  return (
    <AmountInputStep onContinue={handleAmountContinue} onCancel={handleClose} />
  );
}
